/**
 * Forward paper test of "Riptide" (research/r5-weekday, RESULTS.md): when
 * BTC has just had a very big hour, bet the next 15-minute round goes the
 * other way. One minute into a round, if BTC moved 0.70% or more over the 60
 * minutes before the round opened, buy the side AGAINST that move at the
 * Kalshi ask, provided it costs under 60¢, and hold to settlement.
 *
 * Nothing here trades: it records what the rule WOULD have bought and scores
 * it, so the rule is judged only on rounds that did not exist when it was
 * chosen. Pure logic: no network, no storage.
 */
import type { Candle } from '@whaletail/signal-engine';
import { PRICE_RULE_STAKE_DOLLARS, type Side } from './btc15PriceRules';
import {
  summarizeForwardRule,
  type ForwardPassBar,
  type ForwardRuleSummary,
} from './forwardRuleSummary';

export const RIPTIDE_RULE = {
  id: 'R1',
  label: 'Riptide: against a 0.70%+ last-hour BTC move',
  /** Smallest last-hour move that triggers a bet, as a log return. */
  minMove: 0.007,
  /** The side against the move is bought only below this ask (dollars). */
  maxAsk: 0.6,
  /** Whole minutes into the round at which the entry is taken. */
  entryMinute: 1,
  /** Only rounds closing after this instant count (its registration time). */
  registeredAtMs: Date.parse('2026-10-07T01:00:00Z'),
} as const;

/**
 * Riptide buys near 46¢, where a 60% win-rate floor would be meaningless, so
 * its bar is profit only: enough bets, a net above zero, and a range whose
 * lower end is above −1¢.
 */
export const RIPTIDE_PASS_BAR: ForwardPassBar = {
  minBets: 300,
  minWinRate: null,
  minNetPerContract: 0,
  minNetLowerBound: -0.01,
};

/**
 * BTC's move over the 60 minutes before a round opened, as a log return.
 * Matches the research definition exactly: the round's opening level is the
 * OHLC/4 of the one-minute candle that ended at the open, compared with the
 * close of the candle that ended 60 minutes before the open. Candle `time`
 * is the candle's start, in seconds. Null when either candle is missing.
 */
export function lastHourMove(
  candles: readonly Candle[],
  roundOpenMs: number,
): number | null {
  const openSec = Math.floor(roundOpenMs / 1000);
  const last = candles.find((c) => c.time === openSec - 60);
  const ref = candles.find((c) => c.time === openSec - 3660);
  if (!last || !ref || !(ref.close > 0)) return null;
  const level = (last.open + last.high + last.low + last.close) / 4;
  return level > 0 ? Math.log(level / ref.close) : null;
}

/** Why a look at the candles could not measure the last-hour move. */
export type RiptideLookFailure =
  'open-candle-missing' | 'reference-candle-missing' | 'candles-fetch-failed';

/**
 * Why `lastHourMove` would be null for these candles, or null when it would
 * not be. The open candle is checked first. An open candle whose OHLC/4
 * level is not above zero counts as a missing open candle.
 */
export function riptideLookFailure(
  candles: readonly Candle[],
  roundOpenMs: number,
): RiptideLookFailure | null {
  const openSec = Math.floor(roundOpenMs / 1000);
  const last = candles.find((c) => c.time === openSec - 60);
  const ref = candles.find((c) => c.time === openSec - 3660);
  if (!last) return 'open-candle-missing';
  if (!ref || !(ref.close > 0)) return 'reference-candle-missing';
  const level = (last.open + last.high + last.low + last.close) / 4;
  return level > 0 ? null : 'open-candle-missing';
}

/** How long to wait before the one second look at the candles. */
export const RIPTIDE_RECHECK_DELAY_MS = 12_000;
/** No second look if the function has already run this long (Netlify stops it at 30 s). */
export const RIPTIDE_RECHECK_MAX_ELAPSED_MS = 8_000;
/** The second look must land at least this long before minute 1 ends. */
const RIPTIDE_RECHECK_MARGIN_MS = 3_000;

/**
 * Whether the minute-1 check may take a second look after a failed first
 * one. Both must hold: the run is still young, and the look after the wait
 * is still inside minute 1 of the same round, with a margin.
 */
export function canRecheckRiptide(t: {
  startedMs: number;
  nowMs: number;
  roundOpenMs: number;
}): boolean {
  const entryEndMs = t.roundOpenMs + (RIPTIDE_RULE.entryMinute + 1) * 60_000;
  return (
    t.nowMs - t.startedMs < RIPTIDE_RECHECK_MAX_ELAPSED_MS &&
    t.nowMs + RIPTIDE_RECHECK_DELAY_MS <= entryEndMs - RIPTIDE_RECHECK_MARGIN_MS
  );
}

/** The side that bets against the move, or null when the move is too small. */
export function riptideSide(move: number | null): Side | null {
  if (move === null || !Number.isFinite(move)) return null;
  if (move >= RIPTIDE_RULE.minMove) return 'no';
  if (move <= -RIPTIDE_RULE.minMove) return 'yes';
  return null;
}

export interface RiptideDecision {
  marketTicker: string;
  roundOpenMs: number;
  roundCloseMs: number;
  observedAtMs: number;
  /** The last-hour move that triggered the bet, as a log return. */
  move: number;
  side: Side;
  ask: number;
  /**
   * The same side's ask at the first run after the entry, normally one
   * minute later: a realistic-fill stress.
   */
  nextAsk?: number | null;
  /** Whole minutes between the entry and the `nextAsk` reading (1 or 2). */
  nextAskLagMinutes?: number;
  result?: Side;
}

/**
 * How long after the entry the next-ask reading may be taken. Scheduled runs
 * are sometimes skipped; a reading two minutes later is still a fair check.
 */
export const NEXT_ASK_MAX_LAG_MINUTES = 2;

const ROUND_MS = 15 * 60_000;

/**
 * A count of the minute-1 checks that actually ran. Riptide bets rarely, so
 * "no bets" alone can't tell a quiet market from a logger that has stopped:
 * this can. The entry is only ever taken at minute 1, so a round whose check
 * did not run, or could not measure the move, is a round the test never saw.
 */
export interface RiptideChecks {
  /** The first round counted (its open). */
  firstRoundOpenMs: number;
  /** Rounds whose minute-1 check measured the last-hour move. */
  checked: number;
  /** Of those, rounds where the move was big enough to bet against. */
  triggered: number;
  lastRoundOpenMs: number;
  /** The last measured move, as a log return, and when it was measured. */
  lastMove: number;
  lastCheckedMs: number;
}

export interface RiptideCheckIssues {
  /** Open time of the first round this was tracked for. */
  sinceRoundOpenMs: number;
  /** Rounds whose first look failed, by reason. A round is counted once. */
  firstLookFailed: Record<RiptideLookFailure, number>;
  /** Of those rounds, how many were measured on the second look. */
  recovered: number;
  /** Rounds whose first look failed and that got no second look (a guard said no). */
  noSecondLook: number;
  /** The round last counted, so a second run in the same minute can't count it twice. */
  lastRoundOpenMs: number;
  last: {
    roundOpenMs: number;
    atMs: number;
    reason: RiptideLookFailure;
    recovered: boolean;
  };
}

export interface RiptideState {
  schemaVersion: 1;
  decisions: RiptideDecision[];
  lastRunMs: number | null;
  /** Absent until the first check after this was added. */
  checks?: RiptideChecks;
  /** Absent until the first failed look after this was added. */
  checkIssues?: RiptideCheckIssues;
}

export function emptyRiptideState(): RiptideState {
  return { schemaVersion: 1, decisions: [], lastRunMs: null };
}

/** About six decisions a day, so this is several years. */
export const MAX_RIPTIDE_DECISIONS = 10_000;

const LOOK_FAILURES: readonly RiptideLookFailure[] = [
  'open-candle-missing',
  'reference-candle-missing',
  'candles-fetch-failed',
];
const isCount = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** A well-formed `checkIssues`, rebuilt field by field, or undefined. */
function parseCheckIssues(raw: unknown): RiptideCheckIssues | undefined {
  if (raw === null || typeof raw !== 'object') return undefined;
  const r = raw as Partial<RiptideCheckIssues>;
  const f = r.firstLookFailed;
  const l = r.last;
  if (f === null || typeof f !== 'object') return undefined;
  if (l === null || typeof l !== 'object' || l === undefined) return undefined;
  if (
    !isCount(r.sinceRoundOpenMs) ||
    !isCount(r.recovered) ||
    !isCount(r.noSecondLook) ||
    !isCount(r.lastRoundOpenMs) ||
    !LOOK_FAILURES.every((k) => isCount(f[k])) ||
    !isCount(l.roundOpenMs) ||
    !isCount(l.atMs) ||
    !LOOK_FAILURES.includes(l.reason as RiptideLookFailure) ||
    typeof l.recovered !== 'boolean'
  ) {
    return undefined;
  }
  return {
    sinceRoundOpenMs: r.sinceRoundOpenMs,
    firstLookFailed: {
      'open-candle-missing': f['open-candle-missing'],
      'reference-candle-missing': f['reference-candle-missing'],
      'candles-fetch-failed': f['candles-fetch-failed'],
    },
    recovered: r.recovered,
    noSecondLook: r.noSecondLook,
    lastRoundOpenMs: r.lastRoundOpenMs,
    last: {
      roundOpenMs: l.roundOpenMs,
      atMs: l.atMs,
      reason: l.reason as RiptideLookFailure,
      recovered: l.recovered,
    },
  };
}

export function parseRiptideState(raw: string): RiptideState {
  try {
    const parsed = JSON.parse(raw) as Partial<RiptideState>;
    if (parsed?.schemaVersion !== 1 || !Array.isArray(parsed.decisions)) {
      return emptyRiptideState();
    }
    const state: RiptideState = {
      schemaVersion: 1,
      decisions: parsed.decisions,
      lastRunMs: typeof parsed.lastRunMs === 'number' ? parsed.lastRunMs : null,
    };
    const c = parsed.checks;
    const isChecks =
      c !== null &&
      typeof c === 'object' &&
      [
        c.firstRoundOpenMs,
        c.checked,
        c.triggered,
        c.lastRoundOpenMs,
        c.lastMove,
        c.lastCheckedMs,
      ].every((v) => typeof v === 'number' && Number.isFinite(v));
    const withChecks = isChecks ? { ...state, checks: c } : state;
    const issues = parseCheckIssues(parsed.checkIssues);
    return issues ? { ...withChecks, checkIssues: issues } : withChecks;
  } catch {
    return emptyRiptideState();
  }
}

/**
 * Counts a round whose minute-1 check measured the last-hour move. Each
 * round is counted once, however many times the check runs. A check that
 * could not measure the move (a missing candle) is not counted, so it shows
 * up as a missed round.
 */
export function recordRiptideCheck(
  state: RiptideState,
  obs: { nowMs: number; roundOpenMs: number; roundCloseMs: number; move: number | null },
): RiptideState {
  if (obs.roundCloseMs <= RIPTIDE_RULE.registeredAtMs) return state;
  if (obs.move === null || !Number.isFinite(obs.move)) return state;
  const prior = state.checks;
  if (prior && prior.lastRoundOpenMs >= obs.roundOpenMs) return state;
  const fired = riptideSide(obs.move) !== null ? 1 : 0;
  return {
    ...state,
    checks: {
      firstRoundOpenMs: prior?.firstRoundOpenMs ?? obs.roundOpenMs,
      checked: (prior?.checked ?? 0) + 1,
      triggered: (prior?.triggered ?? 0) + fired,
      lastRoundOpenMs: obs.roundOpenMs,
      lastMove: obs.move,
      lastCheckedMs: obs.nowMs,
    },
  };
}

/**
 * Notes why a round's first look at the candles failed, and whether the
 * second look (if any) recovered it. A round is counted once. A round whose
 * first look worked is never passed here.
 */
export function recordRiptideLookIssue(
  state: RiptideState,
  obs: {
    roundOpenMs: number;
    roundCloseMs: number;
    atMs: number;
    /** Why the FIRST look failed. */
    reason: RiptideLookFailure;
    recovered: boolean;
    /** Whether a second look was taken. */
    secondLook: boolean;
  },
): RiptideState {
  if (obs.roundCloseMs <= RIPTIDE_RULE.registeredAtMs) return state;
  const prior = state.checkIssues;
  if (prior && prior.lastRoundOpenMs >= obs.roundOpenMs) return state;
  return {
    ...state,
    checkIssues: {
      sinceRoundOpenMs: prior?.sinceRoundOpenMs ?? obs.roundOpenMs,
      firstLookFailed: {
        'open-candle-missing': 0,
        'reference-candle-missing': 0,
        'candles-fetch-failed': 0,
        ...prior?.firstLookFailed,
        [obs.reason]: (prior?.firstLookFailed[obs.reason] ?? 0) + 1,
      },
      recovered: (prior?.recovered ?? 0) + (obs.recovered ? 1 : 0),
      noSecondLook: (prior?.noSecondLook ?? 0) + (obs.secondLook ? 0 : 1),
      lastRoundOpenMs: obs.roundOpenMs,
      last: {
        roundOpenMs: obs.roundOpenMs,
        atMs: obs.atMs,
        reason: obs.reason,
        recovered: obs.recovered,
      },
    },
  };
}

export interface RiptideChecksSummary {
  /** Counting began with the round that opened at this time. */
  sinceMs: number;
  /** Rounds since then whose minute 1 has passed. */
  rounds: number;
  checked: number;
  triggered: number;
  /** Rounds the minute-1 check never measured: the test did not see them. */
  missed: number;
  lastMove: number;
  lastCheckedMs: number;
}

/** How many rounds the logger should have checked by now, and how many it did. */
export function summarizeRiptideChecks(
  state: RiptideState,
  nowMs: number,
): RiptideChecksSummary | null {
  const c = state.checks;
  if (!c) return null;
  // A round is due once its entry minute has ended.
  const dueBy = (roundOpenMs: number) =>
    roundOpenMs + (RIPTIDE_RULE.entryMinute + 1) * 60_000;
  const rounds =
    nowMs < dueBy(c.firstRoundOpenMs)
      ? 0
      : Math.floor((nowMs - dueBy(c.firstRoundOpenMs)) / ROUND_MS) + 1;
  // The current round may already be checked before it is due.
  const checkedDue = c.checked - (nowMs < dueBy(c.lastRoundOpenMs) ? 1 : 0);
  return {
    sinceMs: c.firstRoundOpenMs,
    rounds,
    checked: c.checked,
    triggered: c.triggered,
    missed: Math.max(0, rounds - checkedDue),
    lastMove: c.lastMove,
    lastCheckedMs: c.lastCheckedMs,
  };
}

const validAsk = (ask: number | null | undefined): ask is number =>
  typeof ask === 'number' && ask > 0 && ask < 1;

/** What this minute's run has to look up, if anything. */
export function riptideStep(
  state: RiptideState,
  round: { roundOpenMs: number; roundCloseMs: number; minute: number },
): 'enter' | 'follow' | 'idle' {
  if (round.roundCloseMs <= RIPTIDE_RULE.registeredAtMs) return 'idle';
  const mine = state.decisions.find((d) => d.roundOpenMs === round.roundOpenMs);
  if (!mine) return round.minute === RIPTIDE_RULE.entryMinute ? 'enter' : 'idle';
  const lag = round.minute - RIPTIDE_RULE.entryMinute;
  return mine.nextAsk === undefined && lag >= 1 && lag <= NEXT_ASK_MAX_LAG_MINUTES
    ? 'follow'
    : 'idle';
}

/** Records the entry, when the move is big enough and the side is cheap enough. */
export function enterRiptide(
  state: RiptideState,
  obs: {
    nowMs: number;
    roundOpenMs: number;
    roundCloseMs: number;
    marketTicker: string;
    move: number | null;
    yesAsk: number | null;
    noAsk: number | null;
  },
): RiptideState {
  const side = riptideSide(obs.move);
  if (side === null || obs.move === null) return state;
  if (state.decisions.some((d) => d.roundOpenMs === obs.roundOpenMs)) return state;
  const ask = side === 'yes' ? obs.yesAsk : obs.noAsk;
  if (!validAsk(ask) || ask >= RIPTIDE_RULE.maxAsk) return state;
  return {
    ...state,
    decisions: [
      ...state.decisions,
      {
        marketTicker: obs.marketTicker,
        roundOpenMs: obs.roundOpenMs,
        roundCloseMs: obs.roundCloseMs,
        observedAtMs: obs.nowMs,
        move: obs.move,
        side,
        ask,
      },
    ].slice(-MAX_RIPTIDE_DECISIONS),
  };
}

/** Records the same side's ask at the first run after the entry. */
export function followRiptide(
  state: RiptideState,
  obs: {
    roundOpenMs: number;
    /** Whole minutes into the round now. */
    minute: number;
    yesAsk: number | null;
    noAsk: number | null;
  },
): RiptideState {
  return {
    ...state,
    decisions: state.decisions.map((d) => {
      if (d.roundOpenMs !== obs.roundOpenMs || d.nextAsk !== undefined) return d;
      const ask = d.side === 'yes' ? obs.yesAsk : obs.noAsk;
      return {
        ...d,
        nextAsk: validAsk(ask) ? ask : null,
        nextAskLagMinutes: obs.minute - RIPTIDE_RULE.entryMinute,
      };
    }),
  };
}

/** Applies Kalshi's official results to closed, unsettled decisions. */
export function settleRiptide(
  state: RiptideState,
  resultsByTicker: ReadonlyMap<string, 'UP' | 'DOWN'>,
  nowMs: number,
): RiptideState {
  return {
    ...state,
    decisions: state.decisions.map((d) => {
      if (d.result !== undefined || d.roundCloseMs >= nowMs - 60_000) return d;
      const withNext = d.nextAsk === undefined ? { ...d, nextAsk: null } : d;
      const actual = resultsByTicker.get(d.marketTicker);
      return actual ? { ...withNext, result: actual === 'UP' ? 'yes' : 'no' } : withNext;
    }),
  };
}

/** Scores Riptide on settled decisions against its own pre-registered bar. */
export function summarizeRiptide(
  decisions: readonly RiptideDecision[],
  stake = PRICE_RULE_STAKE_DOLLARS,
): ForwardRuleSummary {
  return summarizeForwardRule(decisions, RIPTIDE_RULE, RIPTIDE_PASS_BAR, stake);
}
